import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Heading,
  Typography,
  Frame,
  Switch
} from 'components'
import { updateUser, useContent } from 'utils'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'
import firebase from 'utils/firebase'
import { makeStyles, withStyles } from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  switchContainer: {
    marginBottom: theme.spacing(4),
    textAlign: 'center'
  },
  switchItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(3)
  },
  switchControlWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: theme.spacing(1)
  },
  label: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 2)
  },
  statement: {
    fontSize: '1rem',
    marginTop: theme.spacing(1),
    fontWeight: 500,
    textAlign: 'center'
  },
  subheading: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    textAlign: 'center'
  },
  customSwitch: {
    '& .MuiSwitch-track': {
      backgroundColor: 'black !important',
      opacity: 0.5
    },
    '& .Mui-checked .MuiSwitch-thumb': {
      backgroundColor: '#ff9800 !important' // Orange color
    },
    '& .Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#ff9800 !important', // Orange color
      opacity: 0.5
    }
  }
}))

const FreelancerCanadaWorkStatus = ({
  onboarding,
  user,
  stepIndex,
  totalSteps,
  onNext = () => {},
  onSubmit = () => {}
}) => {
  const classes = useStyles()
  
  // Initialize state based on user data if available
  const [eligibleToWork, setEligibleToWork] = useState(
    user?.canadaWorkStatus?.eligibleToWork || false
  )
  
  const [hasPersonaId, setHasPersonaId] = useState(
    user?.canadaWorkStatus?.hasPersonaId || false
  )
  
  const [hasCavco, setHasCavco] = useState(
    user?.canadaWorkStatus?.hasCavco || false
  )

  const { landing } = useContent();

  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'
  
  // Create custom styled Switch using colors from useContent hook
  const CustomSwitch = withStyles({
    switchBase: {
      color: buttonSecondaryColor,
      '&$checked': {
        color: buttonColor,
      },
      '&$checked + $track': {
        backgroundColor: buttonColor,
        opacity: 0.5,
      },
    },
    checked: {},
    track: {
      backgroundColor: buttonSecondaryColor,
      opacity: 0.3,
    },
  })(Switch);

  const handleSubmit = async () => {
    try {
      await updateUser(user, {
        canadaWorkStatus: {
          eligibleToWork,
          hasPersonaId,
          hasCavco
        }
      })
      
      if (onboarding) {
        firebase.analytics().logEvent('user_freelancer_canada_work_status_submit_click')
      }
      
      if (onSubmit) {
        onSubmit()
      }
      
      if (onNext) {
        onNext()
      }
    } catch (error) {
      console.error('Error updating Canada work status:', error)
    }
  }

  return (
    <Frame maxWidth="xs" user={user}>
      <Box mx="auto" maxWidth={430}>
        <Heading style={{ textAlign: 'center' }}>
          What is your status for working in Canada?
        </Heading>

        <Box mt={4}>
          {/* Eligible to Work Switch */}
          <Box className={classes.switchContainer}>
            <Box className={classes.switchItem}>
              <Typography className={classes.statement}>
                I am eligible to work in Canada.
              </Typography>
              <Box className={classes.switchControlWrapper}>
                <Typography className={classes.label}>No/Unsure</Typography>
                <CustomSwitch
                  checked={eligibleToWork}
                  onChange={() => setEligibleToWork(!eligibleToWork)}
                />
                <Typography className={classes.label}>Yes</Typography>
              </Box>
            </Box>
          </Box>

          {/* Persona ID Switch */}
          <Box className={classes.switchContainer}>
            <Box className={classes.switchItem}>
              <Typography className={classes.statement}>
                I have a Persona ID number.
              </Typography>
              <Box className={classes.switchControlWrapper}>
                <Typography className={classes.label}>No</Typography>
                <CustomSwitch
                  checked={hasPersonaId}
                  onChange={() => setHasPersonaId(!hasPersonaId)}
                />
                <Typography className={classes.label}>Yes</Typography>
              </Box>
              <Typography className={classes.subheading}>
                PERSONA-ID is a self-identification system developed by the Canada Media Fund (CMF) 
                for individuals applying for or working on projects funded by the CMF.
              </Typography>
              <Typography 
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  display: 'block',
                  width: '100%'
                }}
                onClick={() => window.open('#', '_blank')}
              >
                Click here to get a Persona ID number
              </Typography>
            </Box>
          </Box>

          {/* CAVCO Number Switch */}
          <Box className={classes.switchContainer}>
            <Box className={classes.switchItem}>
              <Typography className={classes.statement}>
                I have a CAVCO number.
              </Typography>
              <Box className={classes.switchControlWrapper}>
                <Typography className={classes.label}>No</Typography>
                <CustomSwitch
                  checked={hasCavco}
                  onChange={() => setHasCavco(!hasCavco)}
                />
                <Typography className={classes.label}>Yes</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box mx="auto" my={3} maxWidth={280}>
          <Button
            variant="contained"
            type="submit"
            style={{ width: '100%', backgroundColor: buttonColor }}
            onClick={handleSubmit}
          >
            Next
          </Button>
        </Box>
      </Box>

      {onboarding && (
        <ProgressBarFooter
          valid={true}
          stepIndex={stepIndex}
          firstStep={stepIndex}
          numSteps={totalSteps}
          onNext={handleSubmit}
          onPrev={() => {}}
        />
      )}
    </Frame>
  )
}

export default FreelancerCanadaWorkStatus
